import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { getResidentes, getVisitas, getReports, getApelaciones, deleteResidente, updateReportVeredicto } from '../api';
import Header from '../components/Header';
import ResidenteForm from '../components/ResidenteForm';
import VisitItem from '../components/VisitItem';
import HashtagBadge from '../components/HashtagBadge';
import { translateError } from '../utils';

export default function AdminPanel() {
  const { user } = useAuth();
  const [view, setView] = useState('menu');
  const [residentes, setResidentes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [editing, setEditing] = useState(null);
  const [showForm, setShowForm] = useState(false);

  // Historial de visitas
  const [visitas, setVisitas] = useState([]);

  // Reportes (para badge en visitas y sección de reportes)
  const [reportes, setReportes] = useState([]);
  const [reportesMap, setReportesMap] = useState({});
  const [apelacionesMap, setApelacionesMap] = useState({});

  // Cargar datos al montar el panel
  useEffect(() => {
    loadResidentes();
    loadVisitas();
    // eslint-disable-next-line
  }, []);

  const loadResidentes = async () => {
    setLoading(true);
    try {
      const data = await getResidentes();
      setResidentes(data);
    } catch (err) {
      setError(translateError(err));
    }
    setLoading(false);
  };

  const loadVisitas = async () => {
    setLoading(true);
    try {
      const [data, reps] = await Promise.all([getVisitas(), getReports()]);
      setVisitas(data);
      const map = {};
      reps.forEach(r => {
        if (r.visitaId) {
          if (!map[r.visitaId]) map[r.visitaId] = [];
          map[r.visitaId].push(r);
        }
      });
      setReportesMap(map);
    } catch (err) {
      setError(translateError(err));
    }
    setLoading(false);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Eliminar este residente?')) return;
    try {
      await deleteResidente(id);
      loadResidentes();
    } catch (err) {
      setError(translateError(err));
    }
  };

  const handleCreated = () => {
    setShowForm(false);
    setEditing(null);
    loadResidentes();
  };

  const loadReportes = async () => {
    setLoading(true);
    try {
      const [data, apels] = await Promise.all([getReports(), getApelaciones()]);
      setReportes(data);
      const map = {};
      apels.forEach(a => {
        if (a.reporteId) {
          if (!map[a.reporteId]) map[a.reporteId] = [];
          map[a.reporteId].push(a);
        }
      });
      setApelacionesMap(map);
    }
    catch (err) { setError(translateError(err)); }
    setLoading(false);
  };

  const renderMenu = () => (
    <div className="main-content">
      <div className="page-header">
        <h1>Panel de Administración</h1>
        <p>Gestiona residentes, visitas y configuración del sistema</p>
      </div>
      <div className="stats-row">
        <div className="stat-card">
          <div className="stat-icon">👥</div>
          <div className="stat-value">{residentes.length || '-'}</div>
          <div className="stat-label">Residentes</div>
        </div>
        <div className="stat-card">
          <div className="stat-icon">📋</div>
          <div className="stat-value">{visitas.length || '-'}</div>
          <div className="stat-label">Visitas totales</div>
        </div>
        <div className="stat-card">
          <div className="stat-icon">🔲</div>
          <div className="stat-value">Activo</div>
          <div className="stat-label">Estado del sistema</div>
        </div>
      </div>
      <div className="menu-grid">
        <button className="menu-item" onClick={() => { setView('residentes'); loadResidentes(); }}>
          <span className="menu-icon">👥</span>
          <span className="menu-label">Gestionar Residentes</span>
          <span className="menu-desc">Ver, editar o eliminar residentes</span>
        </button>
        <button className="menu-item" onClick={() => { setShowForm(true); }}>
          <span className="menu-icon">➕</span>
          <span className="menu-label">Nuevo Residente</span>
          <span className="menu-desc">Registrar un dueño de apartamento</span>
        </button>
        <button className="menu-item" onClick={() => { setView('visitas'); loadVisitas(); }}>
          <span className="menu-icon">📋</span>
          <span className="menu-label">Historial de Visitas</span>
          <span className="menu-desc">Consultar todas las entradas</span>
        </button>
        <button className="menu-item" onClick={() => { setView('reportes'); loadReportes(); }}>
          <span className="menu-icon">🚩</span>
          <span className="menu-label">Reportes de Vigilancia</span>
          <span className="menu-desc">Anomalías reportadas por vigilantes</span>
        </button>
      </div>
    </div>
  );

  const renderResidentes = () => (
    <div className="main-content">
      <div className="page-header flex-between">
        <div>
          <h1>Gestión de Residentes</h1>
          <p>{residentes.length} residentes registrados</p>
        </div>
        <div className="btn-row">
          <button className="btn btn-outline" onClick={() => setView('menu')}>Volver</button>
          <button className="btn btn-primary" onClick={() => setShowForm(true)}>Nuevo Residente</button>
        </div>
      </div>
      {loading && <div className="spinner" />}
      {error && <div className="alert alert-error">⚠️ {error}</div>}
      {!loading && residentes.length === 0 && (
        <div className="empty-state">
          <div className="empty-icon">👤</div>
          <h3>Sin residentes</h3>
          <p>Aún no hay residentes registrados en el sistema</p>
        </div>
      )}
      <div className="table-container">
        {residentes.length > 0 && (
          <table>
            <thead>
              <tr>
                <th>Residente</th>
                <th>Cédula</th>
                <th>Apto / Piso</th>
                <th>Celular</th>
                <th>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {residentes.map((r) => (
                <tr key={r.id}>
                  <td><strong>{r.nombre_completo}</strong></td>
                  <td>{r.cedula}</td>
                  <td>{r.apartamento} / P{r.piso}</td>
                  <td>{r.celular}</td>
                  <td className="actions-cell">
                    <button className="btn btn-ghost btn-sm" onClick={() => { setEditing(r); setShowForm(true); }}>✏️</button>
                    <button className="btn btn-ghost btn-sm" onClick={() => handleDelete(r.id)} style={{color:'var(--danger)'}}>🗑️</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );

  const renderVisitas = () => (
    <div className="main-content">
      <div className="page-header flex-between">
        <div>
          <h1>Historial de Visitas</h1>
          <p>{visitas.length} visitas registradas</p>
        </div>
        <button className="btn btn-outline" onClick={() => setView('menu')}>Volver</button>
      </div>
      {loading && <div className="spinner" />}
      {!loading && visitas.length === 0 && (
        <div className="empty-state">
          <div className="empty-icon">📋</div>
          <h3>Sin visitas</h3>
          <p>No hay visitas registradas en el sistema</p>
        </div>
      )}
      {visitas.map((v) => (
        <VisitItem key={v.id} visita={v} reports={reportesMap[v.id] || []} />
      ))}
    </div>
  );

  const CAT_COLORS = {
    averia: '#f59e0b', ruido: '#ef4444', dano: '#dc2626',
    estacionamiento: '#6366f1', mascota: '#8b5cf6', basura: '#06b6d4',
    seguridad: '#ef4444', otro: '#64748b', anomalia: '#ef4444'
  };
  const CAT_LABELS = {
    averia: '🔧 Avería', ruido: '🔊 Ruido', dano: '💥 Daño',
    estacionamiento: '🅿️ Estacionamiento', mascota: '🐕 Mascota',
    basura: '🗑️ Basura', seguridad: '🔐 Seguridad', otro: '📌 Otro',
    anomalia: '🚩 Anomalía'
  };

  const [showResidenteModal, setShowResidenteModal] = useState(false);
  const [residenteDetail, setResidenteDetail] = useState(null);

  // Veredicto
  const [veredictoReporteId, setVeredictoReporteId] = useState(null);
  const [veredictoTexto, setVeredictoTexto] = useState('');
  const [veredictoLoading, setVeredictoLoading] = useState(false);

  const openVeredicto = (reporte) => {
    setVeredictoReporteId(reporte.id);
    setVeredictoTexto(reporte.veredicto || '');
  };

  const saveVeredicto = async () => {
    if (!veredictoTexto.trim()) return;
    setVeredictoLoading(true);
    try {
      await updateReportVeredicto(veredictoReporteId, veredictoTexto.trim());
      // Actualizar localmente
      setReportes(prev => prev.map(r =>
        r.id === veredictoReporteId ? { ...r, veredicto: veredictoTexto.trim() } : r
      ));
      setVeredictoReporteId(null);
    } catch (err) { setError(translateError(err)); }
    setVeredictoLoading(false);
  };

  const openResidenteDetail = (hashtag) => {
    const apto = hashtag.replace(/^#/, '');
    const found = residentes.find(r =>
      r.apartamento?.toLowerCase() === apto.toLowerCase()
    );
    if (found) {
      setResidenteDetail(found);
      setShowResidenteModal(true);
    }
  };

  // Conteo de reportes por hashtag
  const getReportCount = (tag) => {
    const apto = tag.replace(/^#/, '').toLowerCase();
    return reportes.filter(r =>
      r.hashtags?.some(h => h.toLowerCase() === apto)
    ).length;
  };

  const getResidenteByTag = (tag) => {
    const apto = tag.replace(/^#/, '');
    return residentes.find(r => r.apartamento?.toLowerCase() === apto.toLowerCase()) || null;
  };

  const renderReportes = () => (
    <div className="main-content">
      <div className="page-header flex-between">
        <div>
          <h1>🚩 Reportes</h1>
          <p>{reportes.length} reportes recibidos</p>
        </div>
        <button className="btn btn-outline" onClick={() => setView('menu')}>Volver</button>
      </div>
      {loading && <div className="spinner" />}
      {!loading && reportes.length === 0 && (
        <div className="empty-state">
          <div className="empty-icon">✅</div>
          <h3>Sin reportes</h3>
          <p>No hay reportes registrados</p>
        </div>
      )}
      {reportes.map((r) => {
        const catColor = CAT_COLORS[r.categoria] || '#ef4444';
        return (
        <div key={r.id} className="history-item" style={{ borderLeftColor: catColor }}>
          <div className="visit-header">
            <div className="visit-name">
              {r.tipo === 'vigilancia' ? '🚩 Reporte de anomalía (vigilancia)' : CAT_LABELS[r.categoria] || '📌 Reporte'}
            </div>
            <span className="badge" style={{
              background: r.tipo === 'vigilancia' ? 'var(--danger)' : catColor,
              color: '#fff', fontSize: '0.7rem'
            }}>
              {r.tipo === 'vigilancia' ? 'Vigilancia' : 'General'}
            </span>
          </div>
          {(r.vigilante_nombre || r.autor_nombre) && (
            <div className="visit-detail">
              <span>{r.tipo === 'vigilancia' ? '🛡️' : '👤'} {r.vigilante_nombre || r.autor_nombre}</span>
            </div>
          )}
          {r.texto && (
            <div className="visit-detail">
              <span>📝 {r.texto}</span>
            </div>
          )}
          {r.hashtags && r.hashtags.length > 0 && (
            <div style={{ display: 'flex', gap: 6, marginTop: 6, flexWrap: 'wrap' }}>
              {r.hashtags.map((tag) => (
                <HashtagBadge key={tag} tag={tag}
                  residente={getResidenteByTag(tag)}
                  reportCount={getReportCount(tag)}
                  onOpenDetail={openResidenteDetail} />
              ))}
            </div>
          )}
          {r.fotos && r.fotos.length > 0 && (
            <div style={{display:'flex',gap:8,marginTop:8,flexWrap:'wrap'}}>
              {r.fotos.map((f, i) => (
                <a key={i} href={f} target="_blank" rel="noopener noreferrer">
                  <img src={f} alt={`Evidencia ${i+1}`} style={{width:64,height:64,borderRadius:8,objectFit:'cover',border:'2px solid var(--border)',cursor:'pointer'}} />
                </a>
              ))}
            </div>
          )}
          {/* Apelaciones del residente */}
          {apelacionesMap[r.id] && apelacionesMap[r.id].length > 0 && (
            <div style={{ marginTop: 12 }}>
              {apelacionesMap[r.id].map((a, ai) => (
                <div key={a.id || ai} style={{
                  background: 'linear-gradient(135deg, rgba(6,182,212,0.06), rgba(6,182,212,0.02))',
                  borderRadius: 12, padding: 14, border: '1px solid rgba(6,182,212,0.2)',
                  marginBottom: ai < apelacionesMap[r.id].length - 1 ? 8 : 0
                }}>
                  <div className="visit-detail" style={{ marginTop: 0 }}>
                    <span style={{ fontWeight: 700, color: 'var(--info)' }}>💬 Apelación</span>
                    <span>👤 {a.residente_nombre || 'Residente'}</span>
                  </div>
                  {a.texto && (
                    <div style={{ margin: '8px 0', padding: 10, background: 'rgba(255,255,255,0.5)', borderRadius: 8, fontSize: '0.84rem', lineHeight: 1.5, whiteSpace: 'pre-wrap' }}>
                      {a.texto}
                    </div>
                  )}
                  {a.fotos && a.fotos.length > 0 && (
                    <div style={{display:'flex',gap:6,flexWrap:'wrap'}}>
                      {a.fotos.map((f, i) => (
                        <a key={i} href={f} target="_blank" rel="noopener noreferrer">
                          <img src={f} alt={`Apelación ${i+1}`} style={{width:56,height:56,borderRadius:6,objectFit:'cover',border:'1px solid rgba(6,182,212,0.3)',cursor:'pointer'}} />
                        </a>
                      ))}
                    </div>
                  )}
                  <div style={{ fontSize: '0.7rem', color: 'var(--text-light)', marginTop: 6 }}>
                    📅 {a.createdAt?.toDate ? new Date(a.createdAt.toDate()).toLocaleString('es-CO') : ''}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Veredicto del admin */}
          {veredictoReporteId === r.id ? (
            <div style={{ marginTop: 12, background: 'linear-gradient(135deg, rgba(16,185,129,0.06), rgba(16,185,129,0.02))', borderRadius: 12, padding: 14, border: '1px solid rgba(16,185,129,0.2)' }}>
              <div className="visit-detail" style={{ marginTop: 0 }}>
                <span style={{ fontWeight: 700, color: 'var(--success)' }}>✅ Veredicto</span>
              </div>
              <textarea value={veredictoTexto} onChange={(e) => setVeredictoTexto(e.target.value)}
                placeholder="Escribe el veredicto o respuesta oficial..."
                rows={3} style={{ width: '100%', borderRadius: 8, padding: 10, border: '1px solid var(--border)', fontFamily: 'inherit', fontSize: '0.85rem', marginTop: 8 }} />
              <div className="btn-row" style={{ marginTop: 10 }}>
                <button className="btn btn-ghost btn-sm" onClick={() => setVeredictoReporteId(null)}>Cancelar</button>
                <button className="btn btn-success btn-sm" onClick={saveVeredicto} disabled={veredictoLoading}>
                  {veredictoLoading ? 'Guardando...' : '✅ Guardar Veredicto'}
                </button>
              </div>
            </div>
          ) : r.veredicto ? (
            <div style={{ marginTop: 12, background: 'linear-gradient(135deg, rgba(16,185,129,0.06), rgba(16,185,129,0.02))', borderRadius: 12, padding: 14, border: '1px solid rgba(16,185,129,0.2)' }}>
              <div className="visit-detail" style={{ marginTop: 0, justifyContent: 'space-between' }}>
                <span style={{ fontWeight: 700, color: 'var(--success)' }}>✅ Veredicto</span>
                <button className="btn btn-ghost btn-sm" onClick={() => openVeredicto(r)} style={{ color: 'var(--text-secondary)', fontSize: '0.7rem' }}>✏️ Editar</button>
              </div>
              <div style={{ margin: '8px 0', padding: 10, background: 'rgba(255,255,255,0.5)', borderRadius: 8, fontSize: '0.84rem', lineHeight: 1.5, whiteSpace: 'pre-wrap' }}>
                {r.veredicto}
              </div>
            </div>
          ) : (
            <div style={{ marginTop: 10 }}>
              <button className="btn btn-ghost btn-sm" onClick={() => openVeredicto(r)}
                style={{ color: 'var(--success)', fontSize: '0.78rem', fontWeight: 600 }}>
                ✅ Añadir Veredicto
              </button>
            </div>
          )}
          <div className="visit-date">📅 {r.createdAt?.toDate ? new Date(r.createdAt.toDate()).toLocaleString('es-CO') : ''}</div>
        </div>
        );
      })}

      {/* Modal de detalle de residente */}
      {showResidenteModal && residenteDetail && (
        <div style={{
          position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh',
          background: 'rgba(0,0,0,0.6)', zIndex: 10000, display: 'flex',
          alignItems: 'center', justifyContent: 'center', padding: 20
        }} onClick={() => setShowResidenteModal(false)}>
          <div className="card" style={{ maxWidth: 420, width: '100%' }}
            onClick={(e) => e.stopPropagation()}>
            <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h2>🏠 #{residenteDetail.apartamento}</h2>
              <button className="btn btn-ghost btn-sm" onClick={() => setShowResidenteModal(false)}>✕</button>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <div style={{
                  width: 56, height: 56, borderRadius: 16,
                  background: 'linear-gradient(135deg, var(--primary), var(--primary-2))',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  color: '#fff', fontSize: '1.5rem', fontWeight: 700
                }}>
                  {(residenteDetail.nombre_completo || '')[0]?.toUpperCase() || '🏠'}
                </div>
                <div>
                  <div style={{ fontWeight: 700, fontSize: '1.05rem' }}>{residenteDetail.nombre_completo}</div>
                  <div style={{ color: 'var(--text-secondary)', fontSize: '0.82rem' }}>Residente</div>
                </div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                <div style={{ padding: 12, background: 'rgba(99,102,241,0.04)', borderRadius: 10 }}>
                  <div style={{ fontSize: '0.68rem', color: 'var(--text-secondary)', textTransform: 'uppercase', fontWeight: 600 }}>Apartamento</div>
                  <div style={{ fontWeight: 700, fontSize: '1rem' }}>{residenteDetail.apartamento}</div>
                </div>
                <div style={{ padding: 12, background: 'rgba(99,102,241,0.04)', borderRadius: 10 }}>
                  <div style={{ fontSize: '0.68rem', color: 'var(--text-secondary)', textTransform: 'uppercase', fontWeight: 600 }}>Piso</div>
                  <div style={{ fontWeight: 700, fontSize: '1rem' }}>P{residenteDetail.piso}</div>
                </div>
                <div style={{ padding: 12, background: 'rgba(99,102,241,0.04)', borderRadius: 10 }}>
                  <div style={{ fontSize: '0.68rem', color: 'var(--text-secondary)', textTransform: 'uppercase', fontWeight: 600 }}>Cédula</div>
                  <div style={{ fontWeight: 600, fontSize: '0.9rem' }}>🪪 {residenteDetail.cedula}</div>
                </div>
                <div style={{ padding: 12, background: 'rgba(99,102,241,0.04)', borderRadius: 10 }}>
                  <div style={{ fontSize: '0.68rem', color: 'var(--text-secondary)', textTransform: 'uppercase', fontWeight: 600 }}>Celular</div>
                  <div style={{ fontWeight: 600, fontSize: '0.9rem' }}>📱 {residenteDetail.celular}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  return (
    <>
      <Header />
      {showForm && (
        <div className="modal-overlay" onClick={(e) => { if (e.target === e.currentTarget) { setShowForm(false); setEditing(null); } }}>
          <div className="modal">
            <h2>{editing ? 'Editar Residente' : 'Nuevo Residente'}</h2>
            <ResidenteForm
              residente={editing}
              onSuccess={handleCreated}
              onCancel={() => { setShowForm(false); setEditing(null); }}
            />
          </div>
        </div>
      )}
      {view === 'menu' && renderMenu()}
      {view === 'residentes' && renderResidentes()}
      {view === 'visitas' && renderVisitas()}
      {view === 'reportes' && renderReportes()}
    </>
  );
}